import type { ChangeEvent } from 'react';
import SecurityCodeInput from 'creative-ui/oktaComponents/SecurityCodeInput';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import MiniControl from 'creative-ui/uikit/MiniControl';
import Modal from 'creative-ui/uikit/Modal';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import popinRenvoieMailActivationSvg from '@/assets/popin-renvoie-mail-activation.svg';
import { Flex } from '@/components/Layout/Flex/Flex';
import Text from '@/components/Typography/Text/Text';
import {
  MFA_OTP_CODE_LENGTH,
  MFA_RESEND_TIMEOUT_SECONDS,
} from '@/constants/mfa';
import { trackClick } from '@/lib/tagging';
import { formatTime } from '@/utils/helpers/timeFormatter';

import { useMfaEmailChallengeManager } from '../hooks/useMfaEmailChallengeManager';

const OTP_CODE_PATTERN = new RegExp(`^[0-9]{${MFA_OTP_CODE_LENGTH}}$`);

interface MfaEmailChallengeFormProps {
  fieldErrors: Record<string, string>;
}

interface FormData {
  code: string;
  rememberDevice: boolean;
}

function MfaEmailChallengeForm({
  fieldErrors,
}: Readonly<MfaEmailChallengeFormProps>) {
  const { t } = useTranslation('mfaEmailChallenge');

  const {
    handleContinue,
    handleTryAnotherMethod,
    useResend,
    showRememberDevice,
    email,
  } = useMfaEmailChallengeManager();

  const {
    remaining,
    disabled: isResendDisabled,
    startResend,
  } = useResend({
    timeoutSeconds: MFA_RESEND_TIMEOUT_SECONDS,
  });

  const [isResending, setIsResending] = useState(false);
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const form = useForm<FormData>({
    defaultValues: {
      code: '',
      rememberDevice: false,
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = form;

  // Get texts with fallback to i18n translations
  const codeLabel = t('form.codeLabel');
  const buttonText = t('form.verifyButtonText');
  const verifyingText = t('form.verifyingText');
  const resendCodeText = t('form.resendCodeText');
  const didntGetCodeText = t('form.didntGetCodeText');
  const emailSentText = t('form.emailSentText');
  const rememberDeviceLabel = t('form.rememberDeviceLabel');
  const verifyViaSmsText = t('footer.verifyViaSmsText');
  const codeRequiredError = t('errors.codeRequired');
  const codeInvalidFormatError = t('errors.codeInvalidFormat');
  const resendFailedError = t('errors.resendFailed');
  const resendModalTitle = t('resendModal.title');
  const resendModalSpamHint = t('resendModal.spamHint');

  // Get SDK errors for code field
  const codeSDKError = fieldErrors.code;

  const onSubmit = async (formData: FormData) => {
    await handleContinue(formData.code, formData.rememberDevice);
  };

  const onResendCode = async () => {
    trackClick('login/email/code/resend');
    setIsResending(true);
    try {
      startResend();
    } catch {
      setError('root', {
        type: 'manual',
        message: resendFailedError,
      });
    } finally {
      setIsResendModalOpen(true);
      setIsResending(false);
    }
  };

  const onTryAnotherMethod = () => {
    trackClick('login/email/code/try-another-method');
    handleTryAnotherMethod();
  };

  const codeErrorMessage =
    errors.code?.message || errors.root?.message || codeSDKError;
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* OTP Code input */}
      <Controller
        name="code"
        control={control}
        rules={{
          required: codeRequiredError,
          pattern: {
            value: OTP_CODE_PATTERN,
            message: codeInvalidFormatError,
          },
        }}
        render={({ field }) => (
          <SecurityCodeInput
            className="u-margin-form"
            name={field.name}
            value={field.value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              field.onChange(e.target.value)
            }
            maxLength={MFA_OTP_CODE_LENGTH}
            label={codeLabel}
          />
        )}
      />
      {codeErrorMessage && (
        <StatusMessage className="u-margin-form" mode="error">
          {codeErrorMessage}
        </StatusMessage>
      )}
      <Text size="sm" align="center" className="u-color-grey">
        {didntGetCodeText}
      </Text>
      <CtaContainer stack>
        <Cta
          className="u-margin-b-s"
          type="ghost-inline"
          onClick={onResendCode}
          disabled={isResendDisabled || isResending}>
          {resendCodeText}
        </Cta>
        <Cta
          className="u-margin-b-s"
          type="ghost-inline"
          onClick={onTryAnotherMethod}>
          {verifyViaSmsText}
        </Cta>
      </CtaContainer>
      {isResendDisabled && (
        <p className="u-fontSize-small u-color-grey u-textCenter u-margin-form">
          {emailSentText} {formatTime(remaining)}
        </p>
      )}
      {showRememberDevice && (
        <Controller
          name="rememberDevice"
          control={control}
          render={({ field }) => (
            <Flex justify="center" className="u-margin-t-m">
              <MiniControl
                name={field.name}
                checked={field.value}
                onChange={field.onChange}>
                {rememberDeviceLabel}
              </MiniControl>
            </Flex>
          )}
        />
      )}
      <CtaContainer stack>
        <Cta
          className="c-submitCta"
          type="primary"
          submit
          disabled={isSubmitting}
          isWaiting={isSubmitting}>
          {isSubmitting ? verifyingText : buttonText}
        </Cta>
      </CtaContainer>

      <Modal
        open={isResendModalOpen}
        onClickClose={() => setIsResendModalOpen(false)}
        modalName={resendModalTitle}>
        <Flex
          justify="center"
          direction="row"
          align="center"
          className="u-margin-b-l">
          <img src={popinRenvoieMailActivationSvg} alt="" />
        </Flex>
        <div className="c-wysiwyg u-margin-b-l">
          <Text align="center" size="sm">
            {t('resendModal.message')}
          </Text>
          <Text align="center" bold>
            {email}
          </Text>
        </div>
        <Text size="sm" accent="soft" align="center">
          {resendModalSpamHint}
        </Text>
      </Modal>
    </form>
  );
}

export default MfaEmailChallengeForm;
